/* global trapFocus, removeTrapFocus */

if (!customElements.get('hotspots-image')) {
  let hotspotsHtmlScrollLockCount = 0;

  function lockHotspotsHtmlScroll() {
    if (hotspotsHtmlScrollLockCount === 0) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    hotspotsHtmlScrollLockCount += 1;
  }

  function unlockHotspotsHtmlScroll() {
    hotspotsHtmlScrollLockCount = Math.max(0, hotspotsHtmlScrollLockCount - 1);
    if (hotspotsHtmlScrollLockCount === 0) {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  }

  class HotspotsImage extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.handleClick.bind(this));
      this.panelCloseHandler =
        this.panelCloseHandler ||
        ((evt) => {
          if (!evt.target.closest('.hotspot__cards-close')) return;
          evt.preventDefault();
          this.closeActiveHotspot();
        });
      this.overlayCloseHandler =
        this.overlayCloseHandler ||
        ((evt) => {
          evt.preventDefault();
          this.closeActiveHotspot();
        });
    }

    disconnectedCallback() {
      const overlay = this.getCardsPanelOverlay();
      if (overlay && this.overlayCloseHandler) {
        overlay.removeEventListener('click', this.overlayCloseHandler);
      }
      if (this.hotspotsOpen) {
        this.hotspotsOpen = false;
        unlockHotspotsHtmlScroll();
      }
    }

    /**
     * Section wrapper that contains this element and the cards panel.
     * @returns {Element|null}
     */
    getHotspotsSectionRoot() {
      return this.closest('[data-hotspots-section]');
    }

    /**
     * @returns {Element|null}
     */
    getCardsPanel() {
      const root = this.getHotspotsSectionRoot();
      if (!root) return null;
      return root.querySelector('[data-hotspots-cards-panel]');
    }

    /**
     * Backdrop overlay (sibling of section root in hotspots.liquid, or inside root if moved).
     * @returns {Element|null}
     */
    getCardsPanelOverlay() {
      const root = this.getHotspotsSectionRoot();
      if (!root) return null;
      const within = root.querySelector('.hotspot__cards-panel-overlay');
      if (within) return within;
      const next = root.nextElementSibling;
      return next?.classList?.contains('hotspot__cards-panel-overlay') ? next : null;
    }

    /**
     * Flickity is initialized on this inner wrapper (see jsc-resets-scripts initHotspotSlider).
     * @param {Element} panel
     * @returns {Element|null}
     */
    getCardsSliderElement(panel) {
      return panel?.querySelector('.hotspot__cards_panel_slider') || null;
    }

    /**
     * Handles 'click' events on the hotspot images.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      if (!evt.target.matches('.hotspot__btn')) return;
      const hotspot = evt.target.parentNode;
      if (this.hotspotsOpen) return;
      this.activateHotspot(hotspot);
    }

    /**
     * After the panel opacity transition finishes closing.
     * @param {object} evt - Event object.
     */
    handlePanelTransitionEnd(evt) {
      if (!this._closingPanel || evt.target !== this._closingPanel || evt.propertyName !== 'opacity') return;

      const donePanel = this._closingPanel;
      donePanel.removeEventListener('transitionend', this.panelTransitionHandler);
      this._closingPanel = null;
      donePanel.style = null;

      if (this.activePanel === donePanel) {
        this.activePanel = null;
      }
      if (!this.hotspotsOpen) {
        this.classList.add('overflow-hidden');
      }
    }

    /**
     * Opens the cards panel and shows every hotspot card.
     * @param {Element} openerHotspot - Hotspot whose button was used to open (for toggle-close and focus return).
     */
    activateHotspot(openerHotspot) {
      const panel = this.getCardsPanel();
      if (!panel) return;

      this.panelTransitionHandler = this.panelTransitionHandler || this.handlePanelTransitionEnd.bind(this);

      if (this._closingPanel) {
        this._closingPanel.removeEventListener('transitionend', this.panelTransitionHandler);
        this._closingPanel.style = null;
        this._closingPanel = null;
      }

      this.lastOpenerHotspot = openerHotspot;
      this.activePanel = panel;
      this.hotspotsOpen = true;
      lockHotspotsHtmlScroll();

      this.checkCardPosition();
      this.classList.remove('overflow-hidden');

      panel.setAttribute('aria-hidden', 'false');
      this.querySelectorAll('.hotspot').forEach((h) => {
        h.classList.add('is-active');
        h.querySelector('.hotspot__btn')?.setAttribute('aria-expanded', 'true');
      });

      // Flickity lives on `.hotspot__cards_panel_slider`, not the outer panel.
      // Match slide to clicked hotspot (Liquid uses 1-based data-hotspot-index).
      const hotspotIndexRaw = parseInt(openerHotspot?.dataset?.hotspotIndex, 10);
      let targetSlideIndex = Number.isFinite(hotspotIndexRaw) ? hotspotIndexRaw - 1 : 0;
      const sliderEl = this.getCardsSliderElement(panel);

      if (window.jQuery && sliderEl) {
        const applyFlickityIndex = () => {
          const $slider = window.jQuery(sliderEl);
          const flickity = $slider.data('flickity');
          if (!flickity) return;

          $slider.flickity('resize');
          $slider.flickity('reposition');

          const slideCount = flickity.slides.length;
          if (slideCount > 0) {
            const idx = Math.max(0, Math.min(targetSlideIndex, slideCount - 1));
            $slider.flickity('select', idx, false, true);
          }
        };

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(applyFlickityIndex);
        });
      }

      trapFocus(this.activePanel, this.activePanel);

      panel.addEventListener('click', this.panelCloseHandler);

      const overlay = this.getCardsPanelOverlay();
      if (overlay) {
        overlay.addEventListener('click', this.overlayCloseHandler);
      }
    }

    /**
     * Hides all cards and deactivates hotspots.
     * @param {object} evt - Event object.
     */
    closeActiveHotspot() {
      if (!this.hotspotsOpen) return;

      const closingPanel = this.activePanel;
      const returnFocusBtn = this.lastOpenerHotspot?.querySelector('.hotspot__btn');

      if (closingPanel) {
        closingPanel.addEventListener('transitionend', this.panelTransitionHandler);
        this._closingPanel = closingPanel;
      }

      this.hotspotsOpen = false;
      unlockHotspotsHtmlScroll();
      this.querySelectorAll('.hotspot').forEach((h) => {
        h.classList.remove('is-active');
        h.querySelector('.hotspot__btn')?.setAttribute('aria-expanded', 'false');
      });
      if (closingPanel) closingPanel.setAttribute('aria-hidden', 'true');

      removeTrapFocus(returnFocusBtn);

      if (closingPanel) {
        closingPanel.removeEventListener('click', this.panelCloseHandler);
      }

      const overlay = this.getCardsPanelOverlay();
      if (overlay) {
        overlay.removeEventListener('click', this.overlayCloseHandler);
      }
    }

    /**
     * Clears inline positioning left over from older layouts.
     */
    checkCardPosition() {
      if (!this.activePanel) return;
      this.activePanel.style.removeProperty('right');
      this.activePanel.style.removeProperty('left');
      this.activePanel.style.removeProperty('top');
      this.activePanel.style.removeProperty('bottom');
      this.activePanel.style.removeProperty('transform');
    }
  }

  customElements.define('hotspots-image', HotspotsImage);
}
